<script setup lang="ts">
import { computed, ref } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import axiosInstance from '@/services/interceptor.services';

interface IStorePromotion {
  id: string;
  name: string;
  status: string;
}

const props = defineProps<{
  storeId: string;
  modelValue: string[];
}>();

const emit = defineEmits<{
  'update:modelValue': [promotionIds: string[]];
}>();

const search = ref('');

const { data: promotions, isPending } = useQuery({
  queryKey: computed(() => ['campaign-target-promotions', props.storeId]),
  queryFn: async () => {
    const params = new URLSearchParams({ pagination: 'false' });
    params.append('restaurantId', props.storeId);
    const r = await axiosInstance.get(`promotions/search?${params}`);
    const d = r.data as any;
    const list = d?.data?.data ?? d?.data ?? d ?? [];
    return (Array.isArray(list) ? list : []) as IStorePromotion[];
  },
  enabled: computed(() => !!props.storeId),
});

const filteredPromotions = computed(() => {
  const list = promotions.value ?? [];
  const q = search.value.trim().toLowerCase();
  if (!q) return list;
  return list.filter((p) => p.name.toLowerCase().includes(q));
});

function isSelected(id: string): boolean {
  return props.modelValue.includes(id);
}
function toggle(id: string) {
  const next = isSelected(id)
    ? props.modelValue.filter((v) => v !== id)
    : [...props.modelValue, id];
  emit('update:modelValue', next);
}
</script>

<template>
  <div class="ctpr">
    <div class="ctpr__toolbar">
      <input
        v-model="search"
        class="ctpr__search"
        type="text"
        placeholder="Rechercher une promotion…"
      />
      <span class="ctpr__count">{{ modelValue.length }} sélectionnée(s)</span>
    </div>

    <div v-if="isPending" class="ctpr__loading">Chargement…</div>
    <div v-else-if="!filteredPromotions.length" class="ctpr__empty">
      Aucune promotion trouvée.
    </div>
    <ul v-else class="ctpr__list">
      <li v-for="p in filteredPromotions" :key="p.id" class="ctpr__item">
        <label class="ctpr__label">
          <span class="ctpr__checkbox">
            <input
              type="checkbox"
              :checked="isSelected(p.id)"
              @change="toggle(p.id)"
            />
            <span class="ctpr__checkbox-box">
              <i class="pi pi-check" />
            </span>
          </span>
          <span>{{ p.name }}</span>
          <span class="ctpr__status" :class="`ctpr__status--${p.status}`">{{
            p.status
          }}</span>
        </label>
      </li>
    </ul>
  </div>
</template>

<style scoped>
.ctpr {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
.ctpr__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
}
.ctpr__search {
  flex: 1;
  min-width: 12rem;
  height: 2.25rem;
  padding: 0 0.75rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.5rem;
  background: var(--nxt-card-bg);
  color: var(--nxt-on-surface);
  font-size: 0.8125rem;
  font-family: inherit;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;
}
.ctpr__search:focus {
  outline: none;
  border-color: var(--nxt-primary);
  box-shadow: 0 0 0 3px rgba(var(--nxt-primary-rgb, 246, 132, 2), 0.15);
}
.ctpr__count {
  font-size: 0.75rem;
  color: var(--nxt-on-surface-muted);
  white-space: nowrap;
}
.ctpr__loading,
.ctpr__empty {
  padding: 1.5rem 0.5rem;
  text-align: center;
  color: var(--nxt-on-surface-muted);
  font-size: 0.8125rem;
}
.ctpr__list {
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 16rem;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.625rem;
  padding: 0.375rem;
}
.ctpr__item {
  border-radius: 0.5rem;
}
.ctpr__label {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.5rem 0.625rem;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface);
  cursor: pointer;
  border-radius: 0.5rem;
}
.ctpr__label:hover {
  background: var(--nxt-surface-mid);
}
.ctpr__checkbox {
  position: relative;
  display: inline-flex;
  flex-shrink: 0;
}
.ctpr__checkbox input {
  position: absolute;
  inset: 0;
  margin: 0;
  opacity: 0;
  cursor: pointer;
}
.ctpr__checkbox-box {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.125rem;
  height: 1.125rem;
  border-radius: 0.3rem;
  border: 1.5px solid var(--nxt-outline-variant);
  background: var(--nxt-card-bg);
  color: transparent;
  font-size: 0.6875rem;
  transition:
    background 0.15s ease,
    border-color 0.15s ease,
    color 0.15s ease;
}
.ctpr__checkbox input:checked ~ .ctpr__checkbox-box {
  background: var(--nxt-primary);
  border-color: var(--nxt-primary);
  color: #fff;
}
[data-theme='dark'] .ctpr__checkbox input:checked ~ .ctpr__checkbox-box {
  background: #ffd60a;
  border-color: #ffd60a;
  color: #0b1320;
}
.ctpr__checkbox input:focus-visible ~ .ctpr__checkbox-box {
  outline: 2px solid var(--nxt-primary);
  outline-offset: 1px;
}
.ctpr__status {
  margin-left: auto;
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--nxt-on-surface-muted);
}
.ctpr__status--active {
  color: #10b981;
}
.ctpr__status--paused {
  color: #f59e0b;
}
</style>
